import { toTimeframeRows, type TimeframeRow } from '@renderer/overlay/Sidepanel/utils/group-by-timeframe'
import type { ReminderItem } from '../../hooks/use-reminders'
import { isPinnedReminder } from '../../components/reminders/utils/pinned'

/**
 * `all` is the widest: every reminder the user hasn't dismissed, resolved or
 * not, and what the Home section previews. `active` drops the resolved ones and
 * is the default; `pinned` narrows that further to the group Active floats on
 * top. Each sits under the one it's a subset of rather than beside it.
 */
export type ReminderFilterType = 'all' | 'active' | 'pinned' | 'resolved'

/**
 * The filters, in menu order, with the labels the chip and the search
 * placeholder both read from.
 *
 * A `Record` rather than a list because it's the only shape TypeScript checks
 * for exhaustiveness — an array of the union accepts a subset, so a fourth
 * filter could be added and silently left out of `normalizeFilter`. Everything
 * that enumerates the filters derives from here.
 */
export const REMINDER_FILTER_LABEL: Record<ReminderFilterType, string> = {
  all: 'All',
  active: 'Active',
  pinned: 'Pinned',
  resolved: 'Resolved',
}

export const REMINDER_FILTERS = Object.keys(REMINDER_FILTER_LABEL) as Array<ReminderFilterType>

/**
 * Coerce a persisted filter to one that still exists. `open` was retired when
 * it folded into `active`, so a value stored before that has to land somewhere
 * rather than fall through `filterItems`' switch. `priority` is the same tab
 * under its old name, so it carries over rather than dropping to the default.
 */
export const normalizeFilter = (filter: string | undefined): ReminderFilterType => {
  if (filter === 'priority') return 'pinned'
  return REMINDER_FILTERS.includes(filter as ReminderFilterType) ? (filter as ReminderFilterType) : 'active'
}

/**
 * Items that belong in a given tab, without imposing an order.
 *
 * `lingering` is the set of reminders completed while the user has been on this
 * surface (see `reminder-linger`): they count as active until the user leaves,
 * so the row stays where it was ticked and can be un-ticked. They're held out of
 * Resolved for as long — one reminder in two tabs at once reads as a duplicate,
 * not as a hand-off.
 */
export const filterItems = (
  items: Array<ReminderItem>,
  filter: ReminderFilterType,
  lingering: ReadonlySet<string>,
): Array<ReminderItem> => {
  const isLingering = (item: ReminderItem) => lingering.has(item.id)
  const isActive = (item: ReminderItem) => item.resolved !== true || isLingering(item)
  switch (filter) {
    case 'all':
      return items.filter((item) => item.state !== 'dismissed')
    case 'active':
      return items.filter((item) => item.state !== 'dismissed' && isActive(item))
    case 'pinned':
      return items.filter((item) => item.state !== 'dismissed' && isActive(item) && isPinnedReminder(item))
    case 'resolved':
      return items.filter((item) => item.state !== 'dismissed' && item.resolved === true && !isLingering(item))
  }
}

/**
 * The tab that holds this reminder, decided the way `filterItems` decides it:
 * Active while it is open or still lingering after a tick, Resolved once it has
 * actually gone. What a caller opening the view on one reminder needs — focusing
 * it on a tab that filters it out shows nothing at all.
 */
export const filterHolding = (reminder: ReminderItem, isLingering: boolean): 'active' | 'resolved' =>
  reminder.resolved === true && !isLingering ? 'resolved' : 'active'

export const byNewestFirst = (a: ReminderItem, b: ReminderItem) => b.createdAt.getTime() - a.createdAt.getTime()

export function pinnedFirst(items: Array<ReminderItem>): Array<ReminderItem> {
  const pinned = items.filter(isPinnedReminder)
  const rest = items.filter((item) => !isPinnedReminder(item))
  return [...pinned, ...rest]
}

export function orderActiveRemindersForDisplay(items: Array<ReminderItem>): Array<ReminderItem> {
  return pinnedFirst([...items].sort(byNewestFirst))
}

/**
 * Order items by a stable, captured order so the list doesn't re-prioritize while
 * the user is browsing. The first time a tab is viewed its order is sorted newest-first
 * and remembered; subsequent live updates keep that order (removed items drop out, new
 * arrivals prepend) instead of reshuffling under the user.
 */
export const orderItemsStably = (
  items: Array<ReminderItem>,
  capturedOrder: Array<string> | undefined,
): Array<ReminderItem> => {
  if (!capturedOrder) return [...items].sort(byNewestFirst)

  const byId = new Map(items.map((item) => [item.id, item]))
  const ordered: Array<ReminderItem> = []
  for (const id of capturedOrder) {
    const item = byId.get(id)
    if (item) {
      ordered.push(item)
      byId.delete(id)
    }
  }
  // New ids prepend so a jot from the header appears under the input. Existing rows stay put.
  return [...[...byId.values()].sort(byNewestFirst), ...ordered]
}

/**
 * Virtualizer rows for the Reminders list (Figma 7652:23138): the Active tab
 * leads with a flat "Pinned" group, then buckets whatever's left by the day it
 * was written. Pinned items are deliberately *not* day-bucketed — the whole
 * point of the group is that it floats above the timeline.
 *
 * Only Active groups that way. Resolved the design draws as day buckets alone,
 * and Pinned is already nothing but pinned items, so a group naming them would
 * label the whole list. All is chronological too — floating a Pinned group
 * there would mix resolved items into it, which reads as a live to-do list that
 * isn't one. Item order inside each group follows the input, so the view's
 * captured stable order survives grouping.
 */
export function toReminderRows(
  items: Array<ReminderItem>,
  filter: ReminderFilterType,
  now?: Date,
): Array<TimeframeRow<ReminderItem>> {
  if (filter !== 'active') {
    return toTimeframeRows(items, (item) => item.createdAt, now)
  }

  const orderedItems = pinnedFirst(items)
  const firstUnpinned = orderedItems.findIndex((item) => !isPinnedReminder(item))
  const pinned = firstUnpinned === -1 ? orderedItems : orderedItems.slice(0, firstUnpinned)
  const rest = firstUnpinned === -1 ? [] : orderedItems.slice(firstUnpinned)

  const pinnedRows: Array<TimeframeRow<ReminderItem>> = pinned.length
    ? [
        { kind: 'label', label: 'Pinned', isFirst: true },
        ...pinned.map((item, index): TimeframeRow<ReminderItem> => ({ kind: 'item', item, itemIndex: index })),
      ]
    : []

  // Day-bucket indices continue past the pinned block so `itemIndex` still
  // reads as a position in the ungrouped list.
  const restRows = toTimeframeRows(rest, (item) => item.createdAt, now).map(
    (row): TimeframeRow<ReminderItem> =>
      row.kind === 'label'
        ? { ...row, isFirst: row.isFirst && pinnedRows.length === 0 }
        : { ...row, itemIndex: row.itemIndex + pinned.length },
  )

  return [...pinnedRows, ...restRows]
}
